import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { fetchCourses, fetchCourseDetails } from '../services/canvasService';
import { useAppMode } from '../contexts/AppModeContext';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import {
  COURSES_STALE_TIME_MS,
  COURSES_GC_TIME_MS,
  COURSE_DETAILS_STALE_TIME_MS,
  COURSE_DETAILS_GC_TIME_MS,
} from '../contexts/canvasQueryConfig';

// Task 5.2a: the cross-child read layer. Every read before this task was
// keyed to ONE active identity -- useCourseData.ts fetches for whichever
// child getActiveCredentials() currently resolves to, and the app
// context-switches between children via switchCanvasUser. A parent with two
// or three children wants them together, which means fetching for several
// canvasUserIds at once.
//
// WHY THIS IS A SEPARATE HOOK, NOT A CHANGE TO useCourseData
// useCourseData is composed into CanvasDataProvider and owns the single
// active identity's reads, including the loading flags and error string the
// whole app renders off. Widening it would put cross-child concerns on
// every existing consumer. This hook is a plain consumer-facing hook (like
// useCanvasData()) that reads AppModeContext/CanvasSessionContext directly
// and is mounted only by callers that actually want several children. It is
// deliberately NOT wired into CanvasDataProvider, ActionPlanPage or
// Dashboard: task 5.2a lands no UI and must not change what any existing
// screen renders or requests. Task 5.2b builds the side-by-side view on top
// of this.
//
// QUERY KEYS -- PER CHILD, EXISTING SHAPE, DELIBERATELY UNCHANGED
//   courses:       ['courses', coursesMode, childUserId]
//   courseDetails: ['courseDetails', coursesMode, childUserId, courseId]
// These are byte-for-byte the shapes useCourseData.ts already uses. The
// tempting alternative -- one key whose identity is parent-plus-child-set,
// e.g. ['coursesForChildren', mode, [idA, idB]] -- is the exact key
// widening task 4.4b proved is a cross-user data leak rather than a cache
// miss, and it breaks two things that are load-bearing today:
//
//   (a) useCourseData's clearCache() (useCourseData.ts:333) removes by the
//       ['courses', coursesMode, userId] PREFIX. Entries written under a
//       set-shaped key would silently stop matching that prefix and would
//       survive a cache clear that is supposed to drop them.
//   (b) The active child's entry here IS the same cache entry the existing
//       single-child useQuery populates. That is what makes mounting this
//       hook for the already-active child cost zero extra requests -- the
//       query client dedupes on key equality. A different key shape would
//       double every request a single-child user makes.
//
// So this hook adds MORE ENTRIES under the existing shape; it never changes
// what an existing entry's identity means. Nothing here mutates
// canvasQueryConfig.ts's staleTime/gcTime values (task 4.4c set them, task
// 4.5 guardrailed them) -- they are imported and reused as-is.
//
// EAGER-FETCH POLICY (see the comment on `courseQueries` below for the
// request arithmetic).

const EMPTY_CHILDREN: CanvasChild[] = [];
const EMPTY_COURSES: any[] = [];
const EMPTY_DETAILS: Record<string | number, any> = {};

export interface CanvasChild {
  /**
   * Normalised to a string, because this value is a query-key segment and
   * ['courses', 'auth', 111] and ['courses', 'auth', '111'] are two
   * different cache entries to TanStack Query. availableUsers carries
   * numeric ids straight from the Canvas API, while the active identity's
   * canvasUserId is a string on every path that rehydrates it from
   * sessionStorage -- normalising here is what lets the active child's
   * entry coincide with the one useCourseData already wrote.
   */
  id: string;
  name?: string;
  [key: string]: any;
}

export interface ChildCourseGroup {
  childUserId: string;
  child: CanvasChild;
  courses: any[];
  isLoading: boolean;
  error: unknown;
}

/**
 * THE canonical child order. There is deliberately one answer to "what
 * order do the children go in", not a per-view one: task 5.2b's side-by-side
 * columns and the digest-email check both consume this function, so a child
 * occupies the same position everywhere it is shown.
 *
 * The order is defined by `availableUsers` -- the list AuthContext builds
 * from the Canvas API's observees response and persists to sessionStorage,
 * and the same list the sidebar's user dropdown already renders in. No
 * re-sorting is applied (not by name, not by id): whatever order Canvas
 * returned the observees in is the order the parent already sees today, and
 * inventing a different one here would make this hook disagree with the
 * dropdown.
 *
 * Duplicates (same id twice) are dropped, keeping first occurrence, so a
 * malformed availableUsers cannot produce two columns for one child or two
 * observers on one query key.
 */
export function getCanonicalChildOrder(availableUsers: any[]): CanvasChild[] {
  if (!Array.isArray(availableUsers) || availableUsers.length === 0) {
    return EMPTY_CHILDREN;
  }

  const seen = new Set<string>();
  const ordered: CanvasChild[] = [];

  for (const user of availableUsers) {
    if (!user || user.id === null || user.id === undefined) continue;
    const id = String(user.id);
    if (seen.has(id)) continue;
    seen.add(id);
    ordered.push({ ...user, id });
  }

  return ordered;
}

interface UseChildrenCourseDataOptions {
  /**
   * Restrict the fetch to a subset of the canonical order. Omit (or pass
   * null) for every child the parent can see. The subset is filtered out of
   * the canonical order rather than iterated in the caller's order, so a
   * caller cannot accidentally define a second ordering.
   */
  childUserIds?: Array<string | number> | null;
}

export function useChildrenCourseData({ childUserIds = null }: UseChildrenCourseDataOptions = {}) {
  const queryClient = useQueryClient();
  const { coursesMode, guestAvailableUsers, pendingUserSelection, getActiveCredentials } = useAppMode();
  const { availableUsers } = useCanvasSession();

  const isDemoMode = coursesMode === 'demo';

  // Which list the children come from is mode-dependent for the same reason
  // the query key carries `coursesMode`: guest mode's observees arrive with
  // the guest token and live on AppModeContext, not on the authenticated
  // session. Demo mode has no cross-child story at all (its mock fixture is
  // a single child), so it resolves to no children and issues no requests.
  const childSource = coursesMode === 'guest' ? guestAvailableUsers : availableUsers;

  // Serialised so an inline `childUserIds={[a, b]}` at the call site (a new
  // array identity every render) doesn't re-derive the child list forever.
  const childFilterKey = childUserIds ? childUserIds.map(String).join('|') : null;

  const canonicalChildren = useMemo(() => {
    if (isDemoMode) return EMPTY_CHILDREN;
    const ordered = getCanonicalChildOrder(childSource);
    if (childFilterKey === null) return ordered;
    const wanted = new Set(childFilterKey === '' ? [] : childFilterKey.split('|'));
    return ordered.filter((child) => wanted.has(child.id));
  }, [isDemoMode, childSource, childFilterKey]);

  const credentials = getActiveCredentials();
  const token = credentials?.token ?? null;
  const rootUrl = credentials?.rootUrl ?? null;

  // The parent holds ONE Canvas token; the child is a parameter on it
  // (fetchCourses(token, userId, rootUrl)). So reading a sibling needs no
  // new authentication -- only the credential set that is already active.
  const coursesQueriesEnabled = !isDemoMode && !pendingUserSelection && !!token && !!rootUrl;

  // EAGER-FETCH DECISION (task 5.2a step 5).
  //
  // Eager: the COURSES LIST for every child, one request each. A cross-child
  // view cannot show anything at all without it, and it is one request per
  // child regardless of how many courses that child has.
  //
  // NOT eager: COURSE DETAILS for non-active children. Details are per
  // course, so they scale with children x courses, not with children. The
  // landing view calls loadAllCourseDetails(true), which is ~6 detail
  // requests for one child at six active courses. Fetching details for
  // three children on first paint would be ~18 detail requests (3 courses
  // lists + ~18 details = ~21) against today's ~7 (1 courses list + ~6
  // details). 4.4c's staleTime (courseDetails 5 min, courses 15 min)
  // absorbs repeat opens but not the first one, so the first paint is where
  // the cost lands.
  //
  // Details are therefore opt-in per child, driven by the caller through
  // loadCourseDetailsForChild / loadCourseDetailsForChildren below. Task
  // 5.2b decides which children are worth the details request and when --
  // that is a presentation question (which column is visible, which child
  // is focused), and this layer should not pre-empt it.
  //
  // Net effect for a single-child parent: 1 courses request and ~6 detail
  // requests -- identical to before this task, and literally the same cache
  // entries, so mounting this hook alongside the existing single-child path
  // adds zero requests.
  const courseQueries = useQueries({
    queries: canonicalChildren.map((child) => ({
      queryKey: ['courses', coursesMode, child.id],
      queryFn: () => fetchCourses(token, child.id, rootUrl),
      enabled: coursesQueriesEnabled,
      staleTime: COURSES_STALE_TIME_MS,
      gcTime: COURSES_GC_TIME_MS,
    })),
  });

  // Grouped by child, in canonical order. useQueries returns its results
  // positionally aligned with the queries it was given, so this zip
  // preserves the canonical order rather than re-deriving one.
  const courseGroups: ChildCourseGroup[] = useMemo(
    () =>
      canonicalChildren.map((child, index) => {
        const query = courseQueries[index];
        return {
          childUserId: child.id,
          child,
          courses: (query?.data as any[]) ?? EMPTY_COURSES,
          isLoading: !!query?.isFetching,
          error: query?.error ?? null,
        };
      }),
    [canonicalChildren, courseQueries]
  );

  const coursesByChild = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    for (const group of courseGroups) {
      grouped[group.childUserId] = group.courses;
    }
    return grouped;
  }, [courseGroups]);

  const isFetchingCourses = courseGroups.some((group) => group.isLoading);

  // Details are held here rather than in the query cache alone because
  // consumers want them as a plain childUserId -> courseId -> details dict
  // (the same shape useCourseData exposes as `courseDetails`, one level
  // deeper). The query cache remains the source of truth for the network
  // side; this is the read-through projection of it.
  const [courseDetailsByChild, setCourseDetailsByChild] = useState<
    Record<string, Record<string | number, any>>
  >({});
  const [loadingDetailsFor, setLoadingDetailsFor] = useState<string[]>([]);
  const [error, setError] = useState('');

  // Drop the projection when the PARENT identity changes (logout/login, or
  // a mode switch). Per-child leakage is impossible here -- the dict is
  // keyed by child -- but a new parent's tree must not inherit the previous
  // parent's children's details. Mirrors the equivalent effect in
  // useCourseData.ts:59.
  useEffect(() => {
    setCourseDetailsByChild({});
  }, [coursesMode, token]);

  const loadCourseDetailsForChild = useCallback(
    async (childUserId: string | number, activeOnly = true) => {
      if (isDemoMode) return EMPTY_DETAILS;

      const id = String(childUserId);
      const creds = getActiveCredentials();
      if (!creds) {
        setError('Canvas API credentials not found');
        return EMPTY_DETAILS;
      }

      // Read the child's courses out of the query cache rather than closing
      // over courseGroups: that keeps this callback stable across renders
      // (courseGroups is rebuilt every render by useQueries) and reads the
      // exact entry the fan-out above populated.
      const childCourses =
        (queryClient.getQueryData(['courses', coursesMode, id]) as any[] | undefined) ?? EMPTY_COURSES;

      const coursesToProcess = activeOnly
        ? childCourses.filter((course) => !course.concluded && !course.access_restricted)
        : childCourses;
      const courseIdsToLoad = coursesToProcess.map((course) => course.api_course_id);

      if (courseIdsToLoad.length === 0) return EMPTY_DETAILS;

      setLoadingDetailsFor((prev) => (prev.includes(id) ? prev : [...prev, id]));
      setError('');

      try {
        // Same key shape, same staleTime/gcTime, same fetchQuery routing as
        // useCourseData's loadAllCourseDetails -- a course whose details
        // were already fetched for the active child is a cache hit here,
        // not a second request.
        const results = await Promise.all(
          courseIdsToLoad.map((courseId) =>
            queryClient
              .fetchQuery({
                queryKey: ['courseDetails', coursesMode, id, courseId],
                queryFn: () => fetchCourseDetails(creds.token, id, creds.rootUrl, courseId),
                staleTime: COURSE_DETAILS_STALE_TIME_MS,
                gcTime: COURSE_DETAILS_GC_TIME_MS,
              })
              .catch((err) => {
                console.error(`Error loading details for child ${id} course ${courseId}:`, err);
                return { id: courseId, error: true };
              })
          )
        );

        const detailsForChild: Record<string | number, any> = {};
        results.forEach((result) => {
          if (result && !result.error) {
            detailsForChild[result.id] = result;
          }
        });

        setCourseDetailsByChild((prev) => ({
          ...prev,
          [id]: { ...(prev[id] || {}), ...detailsForChild },
        }));

        return detailsForChild;
      } catch (err) {
        console.error(`Error loading course details for child ${id}:`, err);
        setError('Failed to load course details. Please try again later.');
        return EMPTY_DETAILS;
      } finally {
        setLoadingDetailsFor((prev) => prev.filter((entry) => entry !== id));
      }
    },
    [isDemoMode, getActiveCredentials, queryClient, coursesMode]
  );

  const loadCourseDetailsForChildren = useCallback(
    async (ids: Array<string | number>, activeOnly = true) => {
      if (!Array.isArray(ids) || ids.length === 0) return EMPTY_DETAILS;
      const grouped: Record<string, Record<string | number, any>> = {};
      await Promise.all(
        ids.map(async (childUserId) => {
          grouped[String(childUserId)] = await loadCourseDetailsForChild(childUserId, activeOnly);
        })
      );
      return grouped;
    },
    [loadCourseDetailsForChild]
  );

  return {
    /** Every child the parent can read, in canonical order. */
    children: canonicalChildren,
    /** Courses grouped by child, positionally in canonical order. */
    courseGroups,
    /** The same courses as a childUserId -> courses lookup. */
    coursesByChild,
    /** childUserId -> courseId -> details, populated on demand. */
    courseDetailsByChild,
    isFetchingCourses,
    loadingDetailsFor,
    error,
    loadCourseDetailsForChild,
    loadCourseDetailsForChildren,
  };
}
